/**
 * MusicPort implementation: HTMLAudioElement-based background music.
 * Three tracks (map, battle, win) preloaded once and switched on scene change.
 * Mute state is shared with web-audio-adapter via localStorage key "gc_audio_muted".
 * First track playback is deferred until first user interaction (autoplay policy).
 */

import type { MusicPort, MusicTrackId } from "../../application/ports.js";
import { activeCampaign } from "../../campaign/index.js";
import type { CampaignAudioConfig } from "../../campaign/types.js";

const MUTE_KEY = "gc_audio_muted";
const DEFAULT_VOLUME = 0.4;
const FADE_MS = 500;

type TrackMap = Record<MusicTrackId, HTMLAudioElement | null>;

export function createWebMusicAdapter(
  trackSources: CampaignAudioConfig["tracks"] = activeCampaign.audio.tracks
): MusicPort & {
  setMuted(muted: boolean): void;
  isMuted(): boolean;
} {
  let muted = false;
  if (typeof localStorage !== "undefined" && localStorage.getItem(MUTE_KEY) === "1") {
    muted = true;
  }

  const tracks: TrackMap = { map: null, battle: null, win: null };
  let current: { id: MusicTrackId; el: HTMLAudioElement } | null = null;
  let pending: { id: MusicTrackId; loop: boolean } | null = null;
  let unlocked = false;
  let fadeTimer: ReturnType<typeof setInterval> | null = null;

  function getTrack(id: MusicTrackId): HTMLAudioElement | null {
    if (typeof Audio === "undefined") return null;
    let el = tracks[id];
    if (!el) {
      try {
        el = new Audio(trackSources[id]);
        el.preload = "auto";
        el.volume = 0;
        tracks[id] = el;
      } catch {
        return null;
      }
    }
    return el;
  }

  function clearFade(): void {
    if (fadeTimer !== null) {
      clearInterval(fadeTimer);
      fadeTimer = null;
    }
  }

  function fadeOut(el: HTMLAudioElement, done: () => void): void {
    clearFade();
    const start = el.volume;
    if (start <= 0.001) { try { el.pause(); el.currentTime = 0; } catch { /* ignore */ } done(); return; }
    const steps = 10;
    const stepMs = FADE_MS / steps;
    let i = 0;
    fadeTimer = setInterval(() => {
      i++;
      const v = Math.max(0, start * (1 - i / steps));
      try { el.volume = v; } catch { /* ignore */ }
      if (i >= steps) {
        clearFade();
        try { el.pause(); el.currentTime = 0; } catch { /* ignore */ }
        done();
      }
    }, stepMs);
  }

  function fadeIn(el: HTMLAudioElement): void {
    clearFade();
    if (muted) { try { el.volume = 0; } catch { /* ignore */ } return; }
    try { el.volume = 0; } catch { /* ignore */ }
    const target = DEFAULT_VOLUME;
    const steps = 10;
    const stepMs = FADE_MS / steps;
    let i = 0;
    fadeTimer = setInterval(() => {
      i++;
      const v = Math.min(target, target * (i / steps));
      try { el.volume = v; } catch { /* ignore */ }
      if (i >= steps) clearFade();
    }, stepMs);
  }

  function startTrack(id: MusicTrackId, loop: boolean): void {
    const el = getTrack(id);
    if (!el) return;
    el.loop = loop;
    if (current && current.id === id) {
      // Same track already playing; just ensure loop flag matches and we're not paused.
      if (el.paused && !muted) {
        try { void el.play(); } catch { /* ignore */ }
      }
      return;
    }
    const begin = (): void => {
      try { el.currentTime = 0; } catch { /* ignore */ }
      try { el.volume = 0; } catch { /* ignore */ }
      if (!muted) {
        try { void el.play(); } catch { /* ignore */ }
        fadeIn(el);
      }
      current = { id, el };
    };
    if (current) {
      const prev = current.el;
      fadeOut(prev, begin);
    } else {
      begin();
    }
  }

  function installAutoplayUnlock(): void {
    if (unlocked) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;
    const unlock = (): void => {
      if (unlocked) return;
      unlocked = true;
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
      if (pending) {
        const p = pending;
        pending = null;
        startTrack(p.id, p.loop);
      } else if (current && current.el.paused && !muted) {
        try { void current.el.play(); } catch { /* ignore */ }
      }
    };
    window.addEventListener("pointerdown", unlock, { once: false });
    window.addEventListener("keydown", unlock, { once: false });
    window.addEventListener("touchstart", unlock, { once: false });
  }

  installAutoplayUnlock();

  return {
    playMusic(trackId: MusicTrackId, opts?: { loop?: boolean }): void {
      const loop = opts?.loop ?? true;
      if (!unlocked) {
        pending = { id: trackId, loop };
        return;
      }
      startTrack(trackId, loop);
    },
    stopMusic(): void {
      pending = null;
      if (!current) return;
      const prev = current.el;
      current = null;
      fadeOut(prev, () => { /* done */ });
    },
    setMuted(v: boolean): void {
      muted = v;
      if (typeof localStorage !== "undefined") {
        if (v) localStorage.setItem(MUTE_KEY, "1");
        else localStorage.removeItem(MUTE_KEY);
      }
      if (current) {
        if (v) {
          try { current.el.pause(); } catch { /* ignore */ }
        } else if (unlocked) {
          try { current.el.volume = DEFAULT_VOLUME; } catch { /* ignore */ }
          try { void current.el.play(); } catch { /* ignore */ }
        }
      }
    },
    isMuted(): boolean { return muted; },
  };
}
